import { useState } from "react";

// react-router components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Collapse from "@mui/material/Collapse";
import MuiLink from "@mui/material/Link";
import Icon from "@mui/material/Icon";

import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "../../hooks/useLocalizedNavigate";

import PointieBox from "../components/PointieBox";
import PointieTypography from "../components/PointieTypography";
import PointieButton from "../components/PointieButton";
import LanguageSwitcher from "../components/LanguageSwitcher";

import DefaultNavbarDropdown from "./DefaultNavbarDropdown";

function DefaultNavbarMobile({ routes, open, actions }) {
  const { t } = useTranslation("nav");
  const lp = useLocalizedPath();
  const [collapse, setCollapse] = useState("");

  const tn = (item) => (item.nameKey ? t(item.nameKey) : item.name);

  const handleSetCollapse = (name) => (collapse === name ? setCollapse(false) : setCollapse(name));

  const renderNavbarItems = routes.map(
    ({ name, nameKey, icon, collapse: routeCollapses, href, route, collapse: navCollapse }) => (
      <DefaultNavbarDropdown
        key={name}
        name={name}
        nameKey={nameKey}
        icon={icon}
        collapseStatus={name === collapse}
        onClick={() => handleSetCollapse(name)}
        href={href}
        route={route}
        collapse={Boolean(navCollapse)}
      >
        <PointieBox sx={{ maxHeight: "15rem", overflowY: "auto" }}>
          {routeCollapses &&
            routeCollapses.map((item) => (
              <PointieBox key={item.name} px={2}>
                {item.collapse ? (
                  <>
                    {item.collapse.map((el) => (
                      <PointieTypography
                        key={el.name}
                        component={el.route ? Link : MuiLink}
                        to={el.route ? lp(`/${el.route}`) : ""}
                        href={el.href ? el.href : ""}
                        target={el.href ? "_blank" : ""}
                        rel={el.href ? "noreferrer" : "noreferrer"}
                        minWidth="11.25rem"
                        display="block"
                        variant="button"
                        color="text"
                        textTransform="capitalize"
                        fontWeight="regular"
                        py={0.625}
                        px={2}
                        sx={({ palette: { grey } }) => ({
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 300ms linear",

                          "&:hover": {
                            backgroundColor: grey[200],
                            color: "#344767",
                          },
                        })}
                      >
                        {tn(el)}
                      </PointieTypography>
                    ))}
                  </>
                ) : (
                  <PointieBox
                    key={item.key}
                    display="block"
                    component={item.route ? Link : MuiLink}
                    to={item.route ? lp(`/${item.route}`) : ""}
                    href={item.href ? item.href : ""}
                    target={item.href ? "_blank" : ""}
                    rel={item.href ? "noreferrer" : "noreferrer"}
                    sx={({ palette: { grey } }) => ({
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 300ms linear",
                      py: 1,
                      px: 1.625,

                      "&:hover": {
                        backgroundColor: grey[200],
                        color: "#344767",

                        "& *": {
                          color: "#344767",
                        },
                      },
                    })}
                  >
                    <PointieTypography
                      display="block"
                      variant="button"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      {tn(item)}
                    </PointieTypography>
                    <PointieTypography
                      display="block"
                      variant="button"
                      color="text"
                      fontWeight="regular"
                      sx={{ transition: "all 300ms linear" }}
                    >
                      {item.description}
                    </PointieTypography>
                  </PointieBox>
                )}
              </PointieBox>
            ))}
        </PointieBox>
      </DefaultNavbarDropdown>
    )
  );

  return (
    <Collapse in={Boolean(open)} timeout="auto" unmountOnExit>
      <PointieBox
        width="calc(100% + 1.625rem)"
        my={2}
        ml={-2}
        sx={{ position: "relative", zIndex: 50 }}
      >
        {renderNavbarItems}
        <PointieBox display="flex" alignItems="center" px={2} mt={1.5} mb={1}>
          <LanguageSwitcher />
        </PointieBox>
        {actions && (
          <PointieBox display="flex" flexDirection="column" gap={1} px={2} mt={1.5}>
            {actions.map((act, idx) =>
              act.type === "internal" ? (
                <PointieButton
                  key={idx}
                  component={Link}
                  to={lp(act.route)}
                  variant="gradient"
                  color={act.color || "info"}
                  size="small"
                  fullWidth
                >
                  {act.icon && <Icon sx={{ mr: 0.5 }}>{act.icon}</Icon>}
                  {act.labelKey ? t(act.labelKey) : act.label}
                </PointieButton>
              ) : (
                <PointieButton
                  key={idx}
                  component="a"
                  href={act.route}
                  target="_blank"
                  rel="noreferrer"
                  variant="gradient"
                  color={act.color || "info"}
                  size="small"
                  fullWidth
                >
                  {act.icon && <Icon sx={{ mr: 0.5 }}>{act.icon}</Icon>}
                  {act.labelKey ? t(act.labelKey) : act.label}
                </PointieButton>
              )
            )}
          </PointieBox>
        )}
      </PointieBox>
    </Collapse>
  );
}

// Typechecking props for the DefaultNavbarMobile
DefaultNavbarMobile.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  open: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
  actions: PropTypes.array,
};

export default DefaultNavbarMobile;
